// Sub-tick fire: the click time inside the tick that is being sampled becomes `FireSample.frac`.

import { Buttons } from "../wasm/types";
import type { FireSampleData } from "../wasm/types";
import { quantizePitch, quantizeYaw } from "./angles";

/**
 * Position of `clickMs` inside the tick `[startMs, startMs + durMs)` as the 0..255 wire value.
 * Clicks before the tick start (queued while the frame had nothing to simulate) map to 0, clicks
 * at or after the end to 255.
 */
export function fireFrac(clickMs: number, startMs: number, durMs: number): number {
  if (!(durMs > 0)) return 0;
  const t = (clickMs - startMs) / durMs;
  if (!(t > 0)) return 0;
  if (t >= 1) return 255;
  return Math.round(t * 255);
}

/** True when the click belongs to this tick or an earlier one (it is not in the future of the tick). */
export function clickIsDue(clickMs: number, startMs: number, durMs: number): boolean {
  return clickMs < startMs + durMs;
}

/** A trigger pull waiting for the tick that contains it. */
export interface PendingFire {
  /** performance.now() of the mouse-down / key-down. */
  timeMs: number;
  /** Live view angles at that instant (radians, including recoil). */
  yaw: number;
  pitch: number;
}

/**
 * Pending trigger pulls (usually 0 or 1). `take` returns the oldest pull whose time falls before
 * the end of the given tick, so a click that lands in a later tick of a multi-tick frame waits for
 * it. Stored in a small ring to avoid allocating per click.
 */
export class FireQueue {
  private readonly items: PendingFire[] = [];

  push(timeMs: number, yaw: number, pitch: number): void {
    // A long backlog means the loop stalled; keep only the newest few.
    if (this.items.length >= 4) this.items.shift();
    this.items.push({ timeMs, yaw, pitch });
  }

  get length(): number {
    return this.items.length;
  }

  /** Removes and returns the oldest pull that is due at the end of the tick, else null. */
  take(startMs: number, durMs: number): PendingFire | null {
    const first = this.items[0];
    if (!first || !clickIsDue(first.timeMs, startMs, durMs)) return null;
    this.items.shift();
    return first;
  }

  clear(): void {
    this.items.length = 0;
  }
}

/**
 * Decides the fire part of one tick's command. A pull due in this tick sets FIRE and fills
 * `out.fire` (frac inside the tick, quantized angles at the click). A pull that belongs to a
 * later tick of the same frame must not show FIRE yet, unless the trigger was already held.
 * Returns the buttons mask to send.
 */
export function resolveFire(
  queue: FireQueue,
  buttons: number,
  prevButtons: number,
  startMs: number,
  durMs: number,
  out: { fire?: FireSampleData },
): number {
  out.fire = undefined;
  const pull = queue.take(startMs, durMs);
  if (pull) {
    out.fire = {
      frac: fireFrac(pull.timeMs, startMs, durMs),
      yaw: quantizeYaw(pull.yaw),
      pitch: quantizePitch(pull.pitch),
    };
    return buttons | Buttons.FIRE;
  }
  if (queue.length > 0 && (prevButtons & Buttons.FIRE) === 0) return buttons & ~Buttons.FIRE;
  return buttons;
}
